import { DataTagType } from "./textGeneration/liveDataTag";

const IMG_PREFIX = "img/";
const SAFE_IMAGE_PROTOCOLS = new Set(["http:", "https:"]);
const FORMULA_DATA_TAG_KEYS = new Set<string>(Object.values(DataTagType));
const FORMULA_NUMBER_FORMATS = new Set(["currency", "percent", "text"]);

export const decodeContentBlockFormulaHtmlEntities = (value: string): string =>
  value.replace(/&(amp|gt|lt|quot);/g, (match, entity: string) => {
    switch (entity) {
      case "amp":
        return "&";
      case "gt":
        return ">";
      case "lt":
        return "<";
      case "quot":
        return '"';
      default:
        return match;
    }
  });

export type ContentBlockFormulaNumberFormat = "currency" | "percent" | "text";

export type ContentBlockFormulaValidationResult =
  { ok: true } | { ok: false; message: string };

export type ContentBlockImageSrcNormalisationResult = {
  src: string | null;
  changed: boolean;
};

const stripQueryAndHash = (value: string): string =>
  value.split(/[?#]/, 1)[0] ?? "";

const isAbsoluteUrl = (value: string): boolean =>
  value.startsWith("http://") || value.startsWith("https://");

const isSafeAbsoluteImageUrl = (value: string): boolean => {
  try {
    return SAFE_IMAGE_PROTOCOLS.has(new URL(value).protocol);
  } catch {
    return false;
  }
};

export const normaliseContentBlockImageSrcForStorage = (
  rawSrc: string | undefined,
): ContentBlockImageSrcNormalisationResult => {
  if (!rawSrc) return { src: null, changed: false };

  const trimmed = rawSrc.trim();
  if (!trimmed || trimmed.toLowerCase().startsWith("data:")) {
    return { src: null, changed: true };
  }

  const withoutQuery = stripQueryAndHash(trimmed);
  if (withoutQuery.startsWith(IMG_PREFIX)) {
    return { src: withoutQuery, changed: withoutQuery !== rawSrc };
  }

  if (withoutQuery.startsWith(`/${IMG_PREFIX}`)) {
    const next = withoutQuery.slice(1);
    return { src: next, changed: next !== rawSrc };
  }

  if (isSafeAbsoluteImageUrl(withoutQuery)) {
    return { src: withoutQuery, changed: withoutQuery !== rawSrc };
  }

  if (withoutQuery.includes(":")) return { src: null, changed: true };

  if (!isAbsoluteUrl(withoutQuery) && !withoutQuery.includes("/")) {
    const next = `${IMG_PREFIX}${withoutQuery}`;
    return { src: next, changed: next !== rawSrc };
  }

  return { src: null, changed: true };
};

export const isContentBlockFormulaNumberFormat = (
  value: string,
): value is ContentBlockFormulaNumberFormat =>
  FORMULA_NUMBER_FORMATS.has(value);

/** Validate the numeric subset used by formula values and text conditions. */
const isValidArithmeticExpression = (expression: string): boolean => {
  let index = 0;

  const skipWhitespace = () => {
    while (index < expression.length) {
      const char = expression[index];
      if (!char || !/\s/.test(char)) break;
      index += 1;
    }
  };

  const parseNumber = (): boolean => {
    skipWhitespace();
    const numberPattern = /(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/y;
    numberPattern.lastIndex = index;
    const match = numberPattern.exec(expression);
    if (!match) return false;
    index = numberPattern.lastIndex;
    return true;
  };

  const parsePrimary = (): boolean => {
    skipWhitespace();
    if (expression[index] === "(") {
      index += 1;
      if (!parseExpression()) return false;
      skipWhitespace();
      if (expression[index] !== ")") return false;
      index += 1;
      return true;
    }
    return parseNumber();
  };

  const parseUnary = (): boolean => {
    skipWhitespace();
    const char = expression[index];
    if (char === "+" || char === "-") {
      index += 1;
      return parseUnary();
    }
    return parsePrimary();
  };

  const parseTerm = (): boolean => {
    if (!parseUnary()) return false;
    for (;;) {
      skipWhitespace();
      const char = expression[index];
      if (char !== "*" && char !== "/" && char !== "%") break;
      index += 1;
      if (!parseUnary()) return false;
    }
    return true;
  };

  const parseExpression = (): boolean => {
    if (!parseTerm()) return false;
    for (;;) {
      skipWhitespace();
      const char = expression[index];
      if (char !== "+" && char !== "-") break;
      index += 1;
      if (!parseTerm()) return false;
    }
    return true;
  };

  if (!parseExpression()) return false;
  skipWhitespace();
  return index === expression.length;
};

const isValidQuotedString = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('"') || !trimmed.endsWith('"')) return false;

  try {
    return typeof JSON.parse(trimmed) === "string";
  } catch {
    // This matches the web renderer's fallback for non-JSON string escapes.
    return true;
  }
};

const splitTernary = (
  value: string,
): { condition: string; whenTrue: string; whenFalse: string } | null => {
  let inQuotes = false;
  let questionIndex = -1;
  let colonIndex = -1;

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    if (char === '"' && value[index - 1] !== "\\") {
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (char === "?" && questionIndex === -1) {
      questionIndex = index;
      continue;
    }
    if (char === ":" && questionIndex !== -1) {
      colonIndex = index;
      break;
    }
  }

  if (questionIndex === -1 || colonIndex === -1) return null;
  return {
    condition: value.slice(0, questionIndex).trim(),
    whenTrue: value.slice(questionIndex + 1, colonIndex).trim(),
    whenFalse: value.slice(colonIndex + 1).trim(),
  };
};

const isValidTextExpression = (expression: string): boolean => {
  if (isValidQuotedString(expression)) return true;

  const ternary = splitTernary(expression);
  if (!ternary) return false;

  const comparison = ternary.condition.match(/^(.*?)(>=|<=|==|!=|>|<)(.*)$/);
  if (!comparison) return false;

  return (
    isValidArithmeticExpression(comparison[1]?.trim() ?? "") &&
    isValidArithmeticExpression(comparison[3]?.trim() ?? "") &&
    isValidQuotedString(ternary.whenTrue) &&
    isValidQuotedString(ternary.whenFalse)
  );
};

/**
 * Validates formulas without importing the server-only HTML sanitizer.
 *
 * @remarks Keep this browser-safe: customer and Admin root-package imports use it.
 * @see docs/guides/HOVER_HELP.md
 * @see apps/web/src/hooks/useCustomFormulas.ts
 */
export const validateContentBlockFormulaExpression = (
  formula: string,
  numberFormat?: ContentBlockFormulaNumberFormat,
): ContentBlockFormulaValidationResult => {
  const trimmed = formula.trim();
  if (!trimmed) return { ok: false, message: "Formula cannot be empty" };

  const keyRegex = /\[([^\]]+)\]/g;
  for (const match of trimmed.matchAll(keyRegex)) {
    const key = match[1] ?? "";
    if (!FORMULA_DATA_TAG_KEYS.has(key)) {
      return { ok: false, message: `Invalid key: ${key}` };
    }
  }

  const formulaWithNumbers = decodeContentBlockFormulaHtmlEntities(
    trimmed.replace(/\[[^\]]+\]/g, "1"),
  );
  const isNumericExpression = isValidArithmeticExpression(formulaWithNumbers);
  const isTextExpression = isValidTextExpression(formulaWithNumbers);
  const supportsTextExpression =
    numberFormat === undefined || numberFormat === "text";

  if (!isNumericExpression && !(supportsTextExpression && isTextExpression)) {
    if (isTextExpression) return { ok: false, message: "Invalid formula" };

    if (!/^[\d.+\-*/()\s%eE<>!=?:"\\]+$/.test(formulaWithNumbers)) {
      return {
        ok: false,
        message: "Formula contains unsupported characters",
      };
    }

    return {
      ok: false,
      message: "Invalid formula",
    };
  }

  return { ok: true };
};
